import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getCompanyNoticeDetails,
  confirmCompanyNotice,
  checkDocumentUrlAvailability,
  confirmCompanyNoticeForITR,
} from 'modules/activityDetails/CompanyNoticeDetails/api/companyNoticeDetailsService'
import { CompanyNoticeDetailsState, ApiCompanyNoticeDetailsResponse } from './types'

const initialState: CompanyNoticeDetailsState = {
  companyNoticeDetailsData: {},
  confirmCompanyNoticeRequest: {},
  getDocumentRequest: {},
}

const companyNoticeDetailsSlice = createSlice({
  name: 'companyNoticeDetails',
  initialState,
  reducers: {
    companyNoticeDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCompanyNoticeDetailsResponse>>
    ) {
      state.companyNoticeDetailsData = action.payload
    },
    clearCompanyNoticeDetails(state) {
      state.companyNoticeDetailsData = initialState.companyNoticeDetailsData
    },
    confirmCompanyNoticeRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.confirmCompanyNoticeRequest = action.payload
    },
    getDocumentRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.getDocumentRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  companyNoticeDetailsLoaded,
  clearCompanyNoticeDetails,
  confirmCompanyNoticeRequested,
  getDocumentRequested,
} = companyNoticeDetailsSlice.actions

export { clearCompanyNoticeDetails }
export default companyNoticeDetailsSlice.reducer

export const loadCompanyNoticeDetailsData = (companyNoticeId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      companyNoticeDetails: () => getCompanyNoticeDetails(companyNoticeId, resultId),
    },
    payloadAction: companyNoticeDetailsLoaded,
  })
}

export const reloadCompanyNoticeDetailsData = (companyNoticeId: Guid, resultId?: Guid) => {
  return createApiAction({
    apiCalls: {
      companyNoticeDetails: () => getCompanyNoticeDetails(companyNoticeId, resultId),
    },
    payloadAction: companyNoticeDetailsLoaded,
    shouldShowGlobalLoading: true,
    getResultToKeep: ({ companyNoticeDetails }) =>
      companyNoticeDetails.companyNoticeDetailsData.result,
  })
}

const handleConfirmNotice =
  (response: ApiMiddlewarePayload): AppThunk =>
  async (dispatch, getState) => {
    dispatch(confirmCompanyNoticeRequested(response))
    const { error, result } = response
    if (!error && result?.confirm) {
      const { result, details } =
        getState().companyNoticeDetails.companyNoticeDetailsData.result?.companyNoticeDetails || {}
      const { id } = details || {}
      const { id: resultId } = result || {}
      id && dispatch(reloadCompanyNoticeDetailsData(id, resultId))
    }
  }

export const confirmCompanyNoticeRequest = (
  activityId: Guid,
  userId?: Guid,
  isITR?: boolean,
  itrId?: string | null
) => {
  return createApiAction({
    apiCalls: {
      confirm: () =>
        isITR && userId
          ? confirmCompanyNoticeForITR(activityId, userId, itrId)
          : confirmCompanyNotice(activityId),
    },
    payloadAction: confirmCompanyNoticeRequested,
    nextAction: handleConfirmNotice,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}

export const checkDocumentDownload = (documentId: Guid) => {
  return createApiAction({
    apiCalls: {
      document: () => checkDocumentUrlAvailability(documentId),
    },
    payloadAction: getDocumentRequested,
    shouldShowGlobalLoading: true,
    enableErrorNotification: true,
  })
}
